'use client';

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import { HospitalityGapError, useHospitalitySource } from '@/src/lib/hospitality/hospitalitySource';
import { HospitalityRefusal } from '@/src/lib/hospitality/createHospitalitySource';
import { useRestaurantSite } from '@/src/lib/hospitality/useRestaurantSite';
import type { AvailabilitySlot, Booking, RestaurantSite } from '@/src/lib/hospitality/wire';
import { BlockSection, EmptyNote, SiteStateNotice, useActiveLocale, useHospitalityMessages } from './shared';
import {
  BOOKING_NOTE_MAX,
  addDays,
  bookingFormErrors,
  bookingStatusStringKey,
  closureOn,
  formatDateTimeIn,
  formatTimeIn,
  isDepositBlocked,
  isSlotTaken,
  localDateIn,
  newIdempotencyKey,
  refusalStringKey,
  type BookingFormValues,
} from './bookingState';

/**
 * The table-booking block: pick a date, a party size and a time the engine
 * offered, then say who is coming.
 *
 * <h3>What this block does not do</h3>
 *
 * <p>It never computes availability. Hours, closures, capacity and
 * combinability belong to the availability engine, and **a time this grid shows
 * is a time the API returned** — nothing is synthesised, rounded or extended.
 * The one thing read from the site descriptor rather than the engine is the
 * closure list, and only to say *why* a date is dead: an empty grid with no
 * explanation reads as "fully booked" when it means "we are shut on Mondays",
 * and the closure's `reason` is rendered exactly as the operator typed it —
 * `Yılbaşı` stays `Yılbaşı`.
 *
 * <p>It never invents a turn time. `durationMinutes` comes from
 * `RestaurantSiteDto.defaultTurnMinutes`; when the operator has set none the
 * query sends null, the engine answers `BOOKING_DURATION_REQUIRED`, and the
 * diner is asked to phone.
 *
 * <h3>Deposits are a blocked state, not a workaround</h3>
 *
 * <p>A channel with `booking.depositPerCover` set refuses a booking made here
 * with `BOOKING_DEPOSIT_REQUIRED`, and it is right to: there is no anonymous
 * payment endpoint for a diner, so this site cannot take the deposit, and a
 * booking created without one would be a table the diner believes is held and
 * the restaurant believes is not paid for. The block renders the refusal as a
 * terminal "please phone us" — no retry button, because the retry would fail
 * identically — and never synthesises `depositPerCover × covers`, which would
 * write a `Payment` for money nobody has collected.
 *
 * <h3>One idempotency key per submission attempt</h3>
 *
 * <p>`Booking` has no `clientRef` column. The `Idempotency-Key` header is the
 * whole mechanism, so the key is minted when a submission starts and **kept
 * across that attempt's retries** — a transport failure leaves it in place, so
 * pressing the button again cannot seat the party twice. It is cleared once the
 * server has answered, success or refusal: an answered request is finished, and
 * a genuinely new attempt deserves a new key.
 */
export interface RestaurantBookingProps {
  heading?: string;
  /** Default party size the form opens on. The slot grid still comes from the
   *  server for whatever the diner picks. */
  defaultPartySize?: number;
  /** Largest party the form will let a diner ask for. Not a capacity rule —
   *  the engine owns that, and answers a party it cannot seat with an empty
   *  grid and a "call us". This only bounds the input. */
  maxPartySize?: number;
  /** How far ahead the date picker opens. */
  daysAhead?: number;
}

type Submission =
  | { status: 'idle' }
  | { status: 'sending' }
  | { status: 'done'; booking: Booking }
  | { status: 'refused'; refusal: HospitalityRefusal }
  | { status: 'blocked'; refusal: HospitalityRefusal }
  | { status: 'unreachable' };

export function RestaurantBooking({
  heading,
  defaultPartySize = 2,
  maxPartySize = 20,
  daysAhead = 180,
}: RestaurantBookingProps) {
  const t = useHospitalityMessages();
  const locale = useActiveLocale();
  const source = useHospitalitySource();
  const state = useRestaurantSite();
  const site: RestaurantSite | null = state.status === 'ready' ? state.site : null;

  const [date, setDate] = useState('');
  const [covers, setCovers] = useState(defaultPartySize);
  const [startAt, setStartAt] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');

  const [slots, setSlots] = useState<AvailabilitySlot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsRefusal, setSlotsRefusal] = useState<HospitalityRefusal | null>(null);
  const [slotsGap, setSlotsGap] = useState(false);
  // Bumped to force a re-read after a slot is taken from under the diner.
  const [reloadToken, setReloadToken] = useState(0);

  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [submission, setSubmission] = useState<Submission>({ status: 'idle' });
  const idempotencyKey = useRef<string | null>(null);

  const timeZone = site?.timeZone ?? '';
  const today = useMemo(() => localDateIn(timeZone), [timeZone]);

  // The date opens on the restaurant's today, not the browser's. Either side of
  // midnight those are different days for a real fraction of visitors.
  useEffect(() => {
    if (today && !date) setDate(today);
  }, [today, date]);

  useEffect(() => {
    if (!source || !site || !date) return;
    let cancelled = false;
    setSlotsLoading(true);
    setSlotsRefusal(null);
    setSlotsGap(false);
    source
      .slots({
        // `locationId` and `channelId` are components of the shared `SlotQuery`
        // and go no further than this call: the BFF sends neither, because the
        // backend resolves both from the hostname. They are passed because the
        // type asks for them, not because they select anything.
        locationId: site.locationId ?? 0,
        channelId: site.channelId,
        date,
        partySize: covers,
        // Null when the operator has configured no turn time — sent as null, so
        // the engine says `BOOKING_DURATION_REQUIRED` rather than this block
        // guessing ninety minutes on a restaurant's behalf.
        durationMinutes: site.defaultTurnMinutes,
      })
      .then((loaded) => {
        if (cancelled) return;
        setSlots(loaded);
        // A time that is no longer offered must not stay selected — submitting
        // it would book a slot the engine did not give us.
        setStartAt((current) => (loaded.some((s) => s.startAt === current) ? current : ''));
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setSlots([]);
        setStartAt('');
        if (error instanceof HospitalityRefusal) setSlotsRefusal(error);
        else if (error instanceof HospitalityGapError) setSlotsGap(true);
        else setSlotsRefusal(null);
      })
      .finally(() => {
        if (!cancelled) setSlotsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [source, site, date, covers, reloadToken]);

  const refusalText = useCallback(
    (refusal: HospitalityRefusal): string => {
      // This app's own words when it has one for the code, and the phase-1
      // envelope's `message` when it does not — including for a code shipped
      // after this build. An unknown refusal must still say something true.
      const key = refusalStringKey(refusal.errorKey);
      return key ? t(key) : refusal.message;
    },
    [t],
  );

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!source) return;
    const values: BookingFormValues = { date, covers, startAt, name, phone, email, note };
    const errors = bookingFormErrors(values);
    setFormErrors(errors);
    if (errors.length) return;

    // Minted once per attempt and kept for its retries. Regenerating it here on
    // every press would be the same as sending none.
    idempotencyKey.current ??= newIdempotencyKey();
    setSubmission({ status: 'sending' });
    try {
      const booking = await source.createBooking(
        {
          // No ids: the BFF sends covers, at, name, phone, email and note, and
          // the backend fills tenant, channel and location from the hostname.
          channelId: site?.channelId ?? 0,
          locationId: site?.locationId ?? 0,
          startAt,
          durationMinutes: null,
          partySize: covers,
          contactName: name.trim(),
          contactPhone: phone.trim(),
          contactEmail: email.trim(),
          note: note.trim(),
        },
        idempotencyKey.current,
      );
      idempotencyKey.current = null;
      setSubmission({ status: 'done', booking });
    } catch (error: unknown) {
      if (error instanceof HospitalityRefusal) {
        // The server answered. This attempt is over either way, so the key is
        // spent; a diner who changes something and tries again is making a new
        // booking and gets a new key.
        idempotencyKey.current = null;
        if (isSlotTaken(error.errorKey)) {
          // Someone took it while the form was being filled in. Say exactly
          // that and put a fresh grid in front of them, rather than a generic
          // error beside a time that no longer exists.
          setStartAt('');
          setReloadToken((token) => token + 1);
        }
        setSubmission({
          status: isDepositBlocked(error.errorKey) ? 'blocked' : 'refused',
          refusal: error,
        });
      } else {
        // A transport failure. The key is deliberately KEPT: the request may
        // well have reached the backend, and pressing the button again must not
        // seat the party twice.
        setSubmission({ status: 'unreachable' });
      }
    }
  }

  const title = heading ?? t('hospitality.booking.heading');

  if (!site) {
    return (
      <BlockSection heading={title}>
        <SiteStateNotice state={state} t={t} />
      </BlockSection>
    );
  }

  // A restaurant with no dining room cannot be booked at all, and saying so is
  // better than an empty grid the diner reads as "fully booked".
  if (site.locationId == null) {
    return (
      <BlockSection heading={title}>
        <EmptyNote>{t('hospitality.booking.noLocation')}</EmptyNote>
      </BlockSection>
    );
  }

  if (submission.status === 'done') {
    return (
      <BlockSection heading={title}>
        <BookingReceipt booking={submission.booking} site={site} locale={locale} t={t} />
      </BlockSection>
    );
  }

  if (submission.status === 'blocked') {
    return (
      <BlockSection heading={title}>
        <Notice tone="blocked">
          <p className="font-medium">{t('hospitality.booking.refusal.deposit')}</p>
          <p className="mt-1">{t('hospitality.booking.callUs')}</p>
        </Notice>
      </BlockSection>
    );
  }

  const closure = closureOn(site.closures, date);
  const maxDate = today ? addDays(today, daysAhead) : undefined;

  return (
    <BlockSection heading={title}>
      <form className="flex flex-col gap-6" onSubmit={submit} noValidate>
        <div className="flex flex-wrap gap-4">
          <Field label={t('hospitality.booking.dateLabel')} htmlFor="booking-date">
            <input
              id="booking-date"
              type="date"
              className={inputClass}
              value={date}
              min={today || undefined}
              max={maxDate}
              onChange={(event) => setDate(event.target.value)}
            />
          </Field>
          <Field label={t('hospitality.booking.coversLabel')} htmlFor="booking-covers">
            <input
              id="booking-covers"
              type="number"
              inputMode="numeric"
              className={inputClass}
              value={covers}
              min={1}
              max={maxPartySize}
              onChange={(event) => setCovers(Math.trunc(Number(event.target.value)) || 0)}
            />
          </Field>
        </div>

        {closure ? (
          <Notice tone="blocked">
            <p className="font-medium">{t('hospitality.booking.closed')}</p>
            {/* The operator's own words, verbatim — never transliterated and
                never translated. */}
            {closure.reason ? <p className="mt-1">{closure.reason}</p> : null}
          </Notice>
        ) : null}

        <fieldset className="border-0 p-0">
          <legend className="text-sm font-medium">{t('hospitality.booking.timeLabel')}</legend>
          <div className="mt-2">
            {slotsLoading ? (
              <EmptyNote>{t('hospitality.loading')}</EmptyNote>
            ) : slotsGap ? (
              <EmptyNote>{t('hospitality.booking.empty')}</EmptyNote>
            ) : slotsRefusal ? (
              <Notice tone="blocked">
                <p>{refusalText(slotsRefusal)}</p>
              </Notice>
            ) : slots.length === 0 ? (
              // Never a bare empty grid: a party the diary cannot seat is told
              // to call, which is the only thing that can actually help them.
              <Notice tone="quiet">
                <p>{t('hospitality.booking.noSlots')}</p>
                <p className="mt-1">{t('hospitality.booking.callUs')}</p>
              </Notice>
            ) : (
              <ul className="flex flex-wrap gap-2" role="list">
                {slots.map((slot) => (
                  <li key={slot.startAt}>
                    <label
                      className={
                        'flex cursor-pointer items-center rounded-md border px-3 py-2 text-sm ' +
                        (startAt === slot.startAt
                          ? 'border-[var(--color-brand-500)] bg-[var(--color-brand-500)] text-[var(--color-text-on-brand)]'
                          : 'border-[var(--color-border)]')
                      }
                    >
                      <input
                        type="radio"
                        name="booking-slot"
                        className="sr-only"
                        value={slot.startAt}
                        checked={startAt === slot.startAt}
                        onChange={() => setStartAt(slot.startAt)}
                      />
                      {/* Formatted in the CHANNEL's zone: a diner reading 16:30
                          because the render happened in New York has been told
                          the wrong time to turn up. */}
                      {formatTimeIn(slot.startAt, site.timeZone, locale)}
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </fieldset>

        <div className="flex flex-col gap-4">
          <Field label={t('hospitality.booking.nameLabel')} htmlFor="booking-name">
            <input
              id="booking-name"
              className={inputClass}
              autoComplete="name"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>
          <Field label={t('hospitality.booking.phoneLabel')} htmlFor="booking-phone">
            <input
              id="booking-phone"
              type="tel"
              className={inputClass}
              autoComplete="tel"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
            />
            <p className="mt-1 text-xs text-[var(--color-text-secondary)]">
              {t('hospitality.booking.phoneHint')}
            </p>
          </Field>
          <Field label={t('hospitality.booking.emailLabel')} htmlFor="booking-email">
            <input
              id="booking-email"
              type="email"
              className={inputClass}
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>
          <Field label={t('hospitality.booking.noteLabel')} htmlFor="booking-note">
            <textarea
              id="booking-note"
              className={inputClass}
              rows={3}
              maxLength={BOOKING_NOTE_MAX}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
            <p className="mt-1 text-xs text-[var(--color-text-secondary)]">
              {t('hospitality.booking.noteHint')}
            </p>
          </Field>
        </div>

        {formErrors.length ? (
          <Notice tone="blocked">
            <ul className="list-disc ps-5">
              {formErrors.map((key) => (
                <li key={key}>{t(key)}</li>
              ))}
            </ul>
          </Notice>
        ) : null}

        {submission.status === 'refused' ? (
          <Notice tone="blocked">
            <p>{refusalText(submission.refusal)}</p>
          </Notice>
        ) : null}

        {submission.status === 'unreachable' ? (
          <Notice tone="blocked">
            <p>{t('hospitality.booking.unreachable')}</p>
          </Notice>
        ) : null}

        <div>
          <button
            type="submit"
            className="rounded-md bg-[var(--color-brand-500)] px-4 py-2 text-sm font-medium text-[var(--color-text-on-brand)] disabled:opacity-60"
            disabled={submission.status === 'sending'}
          >
            {submission.status === 'sending'
              ? t('hospitality.booking.submitting')
              : t('hospitality.booking.submit')}
          </button>
        </div>
      </form>
    </BlockSection>
  );
}

/* ------------------------------------------------------------------ parts */

const inputClass =
  'w-full rounded-md border border-[var(--color-border)] px-3 py-2 text-sm';

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: ReactNode;
}) {
  return (
    <div className="min-w-40 flex-1">
      <label className="text-sm font-medium" htmlFor={htmlFor}>
        {label}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

/** A bordered note. `blocked` carries `role="alert"` so a refusal that appears
 *  after a submit is announced rather than silently drawn. */
function Notice({ tone, children }: { tone: 'blocked' | 'quiet'; children: ReactNode }) {
  return (
    <div
      role={tone === 'blocked' ? 'alert' : undefined}
      className={
        'rounded-md border px-3 py-2 text-sm ' +
        (tone === 'blocked'
          ? 'border-[var(--color-border)] bg-[var(--color-surface-2,transparent)]'
          : 'border-[var(--color-border)] text-[var(--color-text-secondary)]')
      }
    >
      {children}
    </div>
  );
}

/**
 * What a diner sees once the booking exists.
 *
 * <p>The heading is chosen by `Booking.status` and by nothing else.
 * `BookingService.create` reads `booking.requireConfirmation` off the channel
 * and answers `REQUESTED` or `CONFIRMED`; a site that vets its web bookings must
 * not tell somebody their table is held, and a site that does not must not leave
 * them waiting for a call. `Booking.internalNote` is not rendered here or
 * anywhere else on this site — it is staff-only, and the DTO does not even carry
 * it.
 */
export function BookingReceipt({
  booking,
  site,
  locale,
  t,
}: {
  booking: Booking;
  site: Pick<RestaurantSite, 'timeZone'>;
  locale: string;
  t: (key: string, params?: Record<string, string | number>) => string;
}) {
  return (
    <div className="flex flex-col gap-2 text-sm">
      <p className="text-base font-medium">{t(bookingStatusStringKey(booking.status))}</p>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
        <dt className="text-[var(--color-text-secondary)]">
          {t('hospitality.booking.numberLabel')}
        </dt>
        <dd className="font-mono">{booking.bookingNumber}</dd>
        {booking.startAt ? (
          <>
            <dt className="text-[var(--color-text-secondary)]">
              {t('hospitality.booking.whenLabel')}
            </dt>
            {/* The channel's zone, whatever the server's or the browser's is. */}
            <dd>{formatDateTimeIn(booking.startAt, site.timeZone, locale)}</dd>
          </>
        ) : null}
        {booking.partySize != null ? (
          <>
            <dt className="text-[var(--color-text-secondary)]">
              {t('hospitality.booking.coversLabel')}
            </dt>
            <dd>{booking.partySize}</dd>
          </>
        ) : null}
        {booking.noShowFee ? (
          <>
            <dt className="text-[var(--color-text-secondary)]">
              {t('hospitality.booking.noShowFeeLabel')}
            </dt>
            {/* A decimal string, printed as it arrived. Nothing here turns it
                into a JS number, and no currency symbol is hard-coded. */}
            <dd>{booking.noShowFee}</dd>
          </>
        ) : null}
      </dl>
      <p className="text-[var(--color-text-secondary)]">
        {t('hospitality.booking.lookupHint', { number: booking.bookingNumber })}
      </p>
    </div>
  );
}
